<template>
  <div class="objections-dashboard">

    <div v-if="loading" class="objections-loader-wrap">
      <DashboardLoader />
    </div>

    <ElRow v-else type="flex" :gutter="20" class="objections-charts-row">
      <ElCol :md="8">
        <div class="chart-title">Treated vs Untreated</div>
        <DoughnutChart v-if="hasData(treatedUntreatedData)" :data="treatedUntreatedData" />
        <div v-else class="chart-empty-state">{{ fetchError ? "An error has occurred. Try again" : "No records found" }}</div>
      </ElCol>
      <ElCol :md="8">
        <div class="chart-title">Assessment Type</div>
        <DoughnutChart v-if="hasData(assessmentTypeData)" :data="assessmentTypeData" :bg-colors="assessmentTypeColors" />
        <div v-else class="chart-empty-state">{{ fetchError ? "An error has occurred. Try again" : "No records found" }}</div>
      </ElCol>
      <ElCol :md="8">
        <div class="chart-title">Stats</div>
        <DoughnutChart 
          :tooltip-formatter="(value, label) => `${label}: ₦${$numeral(value).format('0,0.00')}`" 
          :label-formatter="formatLabel"
          v-if="hasData(statsData)"
          :data="statsData" />
        <div v-else class="chart-empty-state">{{ fetchError ? "An error has occurred. Try again" : "No records found" }}</div>
      </ElCol>
    </ElRow>
  </div>
</template>

<script>
import { Row, Col } from "element-ui";
import DoughnutChart from "@/components/Dashboard/DoughnutChart.vue";
import DashboardLoader from "@/components/Dashboard/DashboardLoader.vue";
import constants from "@/constants";

const LARGE_UNITS = [
  { value: 1e18, suffix: 'Qn' }, // quintillion
  { value: 1e15, suffix: 'Q' },  // quadrillion
];
export default {
  name: "DashboardObjection",

  components: {
    ElRow: Row,
    ElCol: Col,
    DoughnutChart,
    DashboardLoader,
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },
    startDate: {
      type: String,
      default: "",
    },
    endDate: {
      type: String,
      default: "",
    },
    taxRegionId: {
      type: [Number, String],
      default: 0,
    },
    taxOfficeId: {
      type: [Number, String],
      default: "",
    },
  },

  data() {
    return {
      loading: false,
      initialized: false,
      treatedUntreatedData: null,
      assessmentTypeData: null,
      statsData: null,
      assessmentTypeColors: [],
      fetchError: false
    };
  },

  watch: {
    active(isActive) {
      if (isActive && !this.initialized) {
        this.fetchAll();
      }
    },
     loading(isLoading) {
      this.$emit("loading-change", isLoading);
    },
  },

  mounted() {
    if (this.active) {
      this.fetchAll();
    }
  },

  methods: {
    formatLabel(value) {
      const num = Number(value) || 0;
      const unit = LARGE_UNITS.find((u) => Math.abs(num) >= u.value);

      // Beyond trillions, Intl's compact notation has no suffix
      if (unit) {
        const scaled = new Intl.NumberFormat('en', {
          maximumFractionDigits: 1,
        }).format(num / unit.value);
        return `₦${scaled}${unit.suffix}`;
      }

      return `₦${new Intl.NumberFormat('en', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(num)}`;
    },
    notification(message, icon, color) {
      this.$notify({
        message,
        icon,
        horizontalAlign: "right",
        verticalAlign: "top",
        type: color,
      });
    },

    reload() {
      this.fetchAll();
    },

    hasData(chartData) {
      if (!chartData || !Array.isArray(chartData.dataset)) return false;
      return chartData.dataset.some(
        (series) => Array.isArray(series.data) && series.data.some((value) => Number(value) > 0)
      );
    },

    buildFilters(extra = []) {
      const filters = [
        { field: "assessmentGroup.tax_audit_id", cond_op: "IS_NULL" },
      ];

      if (this.startDate) {
        filters.push({ field: "created_at", value: `${this.startDate} 00:00:00`, cond_op: ">=" });
      }
      if (this.endDate) {
        filters.push({ field: "created_at", value: `${this.endDate} 23:59:59`, cond_op: "<=" });
      }
      if (this.taxRegionId) {
        filters.push({ field: "assessmentGroup.taxPayer.preferredTaxOffice.region_id", value: this.taxRegionId, cond_op: "=" });
      }
      if (this.taxOfficeId) {
        filters.push({ field: "assessmentGroup.taxPayer.preferred_tax_office_id", value: this.taxOfficeId, cond_op: "=" });
      }

      return [...filters, ...extra];
    },

    fetchObjections(listParams) {
      return this.$http
        .post("tax/objections", { method: "GET", params: listParams }, { params: { skipLoader: true } })
        .then(
          (response) => {
            console.log("response", response)
            if (response.data && !response.data.error) {
              return response.data;
            }

            if(response.data && response.data.error){
              this.fetchError = true;
            }
            this.notification(
              (response.data && response.data.message) || "Error fetching objections",
              "error",
              "danger"
            );
            return { list: [] };
          },
          (error) => {
            this.fetchError = true;
            this.notification(error.message, "error", "danger");
            return { list: [] };
          }
        );
    },

    async fetchTreatedUntreated() {
      const treatmentAgeAggs =
        '[{"field": "treatment_age", "func": "<", "value": 30}, {"field": "treatment_age", "func": "<", "value": 60}, {"field": "treatment_age", "func": "<", "value": 90}, {"field": "treatment_age", "func": ">=", "value": 90}]';

      const treatedFilters = this.buildFilters([{ field: "treated_at", cond_op: "IS_NOT_NULL" }]);
      const untreatedFilters = this.buildFilters([{ field: "treated_at", cond_op: "IS_NULL" }]);

      const treatedParams = `?exclude_main_model_data_on_agg=1&aggs=${treatmentAgeAggs}&filters=${JSON.stringify(
        treatedFilters
      )}`;
      const untreatedParams = `?exclude_main_model_data_on_agg=1&aggs=${treatmentAgeAggs}&filters=${JSON.stringify(
        untreatedFilters
      )}`;

      const [treatedRes, untreatedRes] = await Promise.all([
        this.fetchObjections(treatedParams),
        this.fetchObjections(untreatedParams),
      ]);

      const totalFromAgingRow = (row) => {
        const zeroed = row || {};
        const lessThan90 = Number(zeroed["<90"]) || 0;
        const greaterThanOrEqual90 = Number(zeroed[">=90"]) || 0;
        return lessThan90 + greaterThanOrEqual90;
      };

      const counts = {};
      const treated = totalFromAgingRow(treatedRes.list && treatedRes.list[0]);
      const untreated = totalFromAgingRow(untreatedRes.list && untreatedRes.list[0]);

      if (treated > 0) counts.Treated = treated;
      if (untreated > 0) counts.Untreated = untreated;

      this.treatedUntreatedData = {
        labels: Object.keys(counts),
        dataset: [{ label: "Objections", data: Object.values(counts) }],
      };
    },
    generateColors(count) {
      const colors = constants.doughnutChartBgColors;
      while (colors.length < count) {
        const randomColor = "#" + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, "0");
        colors.push(randomColor);
      }
      return colors;
    },
    async fetchAssessmentType() {
      const filters = this.buildFilters();
      const params = `&embeds=["assessmentGroup.type"]&aggs=[{"field": "id", "func": "count", "alias": "total_count"}, {"field": "da_amount"}, {"field": "amount_suggested", "func": "sum", "alias": "total_amount_suggested"}]&group_by=[{"field": "assessmentGroup.type_id"}]&filters=${JSON.stringify(
        filters
      )}`;

      const res = await this.fetchObjections(params);
      const assessmentTypes = {};
      (res.list || []).forEach((item) => {
        const type = item.assessment_group && item.assessment_group.type;
        const label = (type && type.name) || "-";
        assessmentTypes[label] = (assessmentTypes[label] || 0) + (Number(item.total_count) || 0);
      });
      
      const labels = Object.keys(assessmentTypes);
      this.assessmentTypeData = {
        labels,
        dataset: [{ label: "Objections", data: Object.values(assessmentTypes) }],
      };
      this.assessmentTypeColors = this.generateColors(labels.length);
    },

    async fetchStats() {
      const filters = this.buildFilters();
      const params = `&embeds=["status"]&aggs=[{"field": "id", "func": "count", "alias": "total_count"}, {"field": "da_amount"}, {"field": "amount_suggested", "func": "sum", "alias": "total_amount_suggested"}]&group_by=[{"field": "status_id"}]&filters=${JSON.stringify(
        filters
      )}`;

      const res = await this.fetchObjections(params);

      let totalAssessmentAmount = 0;
      let totalSuggestedAmount = 0;
      (res.list || []).forEach((row) => {
        totalAssessmentAmount += Number(row.total_assessment_value) || 0;
        totalSuggestedAmount += Number(row.total_amount_suggested) || 0;
      });

      const stats = {};
      if (totalAssessmentAmount > 0) stats["Total Assessment Value"] = totalAssessmentAmount;
      if (totalSuggestedAmount > 0) stats["Total Proposed Amount"] = totalSuggestedAmount;

      this.statsData = {
        labels: Object.keys(stats),
        dataset: [{ label: "Amount (₦)", data: Object.values(stats) }],
      };
    },

    async fetchAll() {
      this.loading = true;
      this.initialized = true;
      this.fetchError = false;
      try {
        await Promise.all([this.fetchTreatedUntreated(), this.fetchAssessmentType(), this.fetchStats()]);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #98a2b3;
  font-size: 14px;
}

.objections-dashboard {
  width: 100%;
}

.objections-loader-wrap {
  position: relative;
  min-height: 400px;
}

.objections-charts-row {
  flex-wrap: wrap;
}

.chart-title {
  font-weight: 600;
  font-size: 14px;
  color: #475367;
  margin-bottom: 10px;
  margin-top: 10px;
  text-align: center;
}

@media (max-width: 992px) {
  .objections-charts-row {
    .el-col {
      margin-bottom: 20px;
    }
  }
}
</style>